import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Loader2, Search, Plus, Eye, CheckCircle2, Ban } from "lucide-react";
import { toast } from "sonner";
import { adminApi, apiErr } from "@/lib/api";
import { StatusBadge } from "./AdminOverview";

const fmtINR = (n) => `₹${(n || 0).toLocaleString("en-IN")}`;
const TABS = [["all", "All"], ["active", "Active"], ["trial", "Trial"], ["pending", "Pending"], ["suspended", "Suspended"], ["churned", "Churned"]];

export default function AdminClients() {
  const [tab, setTab] = useState("all");
  const [search, setSearch] = useState("");
  const [clients, setClients] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const nav = useNavigate();

  const load = () => {
    adminApi.get(`/admin/clients`, { params: { status: tab, search: search || undefined } }).then((r) => setClients(r.data)).catch(() => setClients([]));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const act = async (id, action, label) => {
    try { await adminApi.post(`/admin/clients/${id}/${action}`); toast.success(label); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const impersonate = async (id) => {
    try {
      const { data } = await adminApi.post(`/admin/clients/${id}/impersonate`);
      toast.success(`Logging in as ${data.company_name}...`);
      setTimeout(() => { window.location.href = "/"; }, 500);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><h1 className="text-2xl font-bold font-display">Clients</h1><p className="text-sm text-slate-500">Manage every company on the platform.</p></div>
        <Button style={{ background: "#7C3AED" }} onClick={() => setShowCreate(true)} data-testid="admin-new-client"><Plus className="w-4 h-4 mr-1" /> New Client</Button>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <Tabs value={tab} onValueChange={setTab}>
          <TabsList className="flex-wrap h-auto">{TABS.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>)}</TabsList>
        </Tabs>
        <div className="relative w-64 max-w-full">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search company / email" className="pl-9" data-testid="admin-client-search" />
        </div>
      </div>

      <Card className="border shadow-none">
        {!clients ? <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: "#7C3AED" }} /></div> :
          clients.length === 0 ? <div className="py-16 text-center text-sm text-slate-400" data-testid="admin-clients-empty">No clients in this view.</div> :
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="text-left text-xs text-slate-400 border-b"><th className="px-4 py-2">Company</th><th className="px-4 py-2">Contact</th><th className="px-4 py-2">Plan</th><th className="px-4 py-2">Calls</th><th className="px-4 py-2">Leads</th><th className="px-4 py-2">Spend/mo</th><th className="px-4 py-2">Status</th><th className="px-4 py-2 text-right">Actions</th></tr></thead>
                <tbody>
                  {clients.map((c) => (
                    <tr key={c.id} className="border-b hover:bg-slate-50 dark:hover:bg-white/5" data-testid={`admin-client-row-${c.id}`}>
                      <td className="px-4 py-3 font-medium cursor-pointer" onClick={() => nav(`/admin/clients/${c.id}`)}>{c.company_name}</td>
                      <td className="px-4 py-3 text-slate-500">{c.contact_email}</td>
                      <td className="px-4 py-3">{c.plan}</td>
                      <td className="px-4 py-3">{c.calls_this_month}</td>
                      <td className="px-4 py-3">{c.leads_count}</td>
                      <td className="px-4 py-3">{fmtINR(c.monthly_spend_inr)}</td>
                      <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {c.status === "pending" && <Button size="sm" variant="outline" className="h-7 text-emerald-600" onClick={() => act(c.id, "approve", "Client approved")} data-testid={`admin-approve-${c.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Approve</Button>}
                          {c.status === "active" && <Button size="sm" variant="outline" className="h-7 text-red-600" onClick={() => act(c.id, "suspend", "Client suspended")} data-testid={`admin-suspend-${c.id}`}><Ban className="w-3.5 h-3.5 mr-1" />Suspend</Button>}
                          {c.status === "suspended" && <Button size="sm" variant="outline" className="h-7 text-emerald-600" onClick={() => act(c.id, "activate", "Client re-activated")} data-testid={`admin-activate-${c.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1" />Activate</Button>}
                          <Button size="sm" variant="ghost" className="h-7" onClick={() => impersonate(c.id)} data-testid={`admin-impersonate-${c.id}`}><Eye className="w-3.5 h-3.5 mr-1" />Login as</Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>}
      </Card>

      <CreateClientDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />
    </div>
  );
}

function CreateClientDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState({ company_name: "", contact_name: "", contact_email: "", contact_mobile: "", plan: "Starter", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async () => {
    if (!f.company_name || !f.contact_email) { toast.error("Company name and email are required"); return; }
    setBusy(true);
    try { const { data } = await adminApi.post("/admin/clients", f); toast.success(`Client created (temp password: ${data.temp_password})`); onCreated(); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="admin-create-client-dialog">
        <DialogHeader><DialogTitle>New Client</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Company name</Label><Input value={f.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid="create-company" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contact name</Label><Input value={f.contact_name} onChange={(e) => set("contact_name", e.target.value)} /></div>
            <div><Label>Mobile</Label><Input value={f.contact_mobile} onChange={(e) => set("contact_mobile", e.target.value)} /></div>
          </div>
          <div><Label>Email (login)</Label><Input type="email" value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid="create-email" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Plan</Label>
              <Select value={f.plan} onValueChange={(v) => set("plan", v)}><SelectTrigger data-testid="create-plan"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Starter">Starter</SelectItem><SelectItem value="Growth">Growth</SelectItem><SelectItem value="Professional">Professional</SelectItem><SelectItem value="Enterprise">Enterprise</SelectItem></SelectContent></Select>
            </div>
            <div><Label>Temp password</Label><Input value={f.password} onChange={(e) => set("password", e.target.value)} placeholder="client123" /></div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button style={{ background: "#7C3AED" }} onClick={submit} disabled={busy} data-testid="create-client-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & Activate"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
